import { useContext } from 'react'
import { ChevronDown } from 'lucide-react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER } from './internal/presets'
import { SiderContext } from './Layout'
import Tooltip from './Tooltip'

const Item = ({ children }) => children
const SubMenu = ({ children }) => children
const ItemGroup = ({ children }) => children
const MenuDivider = () => (
  <li role="separator" className={cn('ui-menu-item-divider my-1 border-t', BORDER)} />
)

/**
 * antd's inline menu is built from `li` rows, not buttons, and the existing
 * sidemenu CSS targets exactly that shape: `.ui-menu-item` and
 * `.ui-menu-submenu-title` are the flex rows, the icon is their first child
 * span, and `.ui-menu-submenu-arrow` is positioned against them. Keeping the
 * structure identical is what lets 74 stylesheet rules survive the swap.
 */
const rowClasses = (extra) =>
  cn(
    'flex min-h-[var(--control-height)] cursor-pointer select-none items-center gap-2 rounded-[var(--radius-xs)] px-3',
    'text-sm font-normal font-[family-name:var(--font-sans)] text-[var(--foreground)]',
    extra
  )

const activateOnKey = (event, run) => {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  run()
}

/** antd shows the label in a right-side tooltip once the sider is collapsed. */
const CollapsedLabel = ({ collapsed, label, children }) => {
  if (!collapsed || !label) return children
  return (
    <Tooltip title={label} placement="right">
      {children}
    </Tooltip>
  )
}

const SubmenuArrow = ({ open }) => (
  <ChevronDown
    aria-hidden="true"
    size={16}
    strokeWidth={2}
    className={cn(
      'ui-menu-submenu-arrow pointer-events-none absolute right-3 top-1/2 -translate-y-1/2',
      'shrink-0 text-[var(--muted-foreground)]',
      'transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]',
      'motion-reduce:transition-none',
      open ? 'rotate-180' : 'rotate-0'
    )}
  />
)

/**
 * antd Menu, `items`-driven. Covers vertical and inline modes with submenus at
 * any depth, controlled or uncontrolled `selectedKeys` / `openKeys`, and the
 * collapsed sider behavior (icons only, label in a tooltip).
 */
const Menu = ({
  items = [],
  mode = 'vertical',
  selectedKeys,
  defaultSelectedKeys,
  openKeys,
  defaultOpenKeys,
  onClick,
  onSelect,
  onOpenChange,
  inlineCollapsed,
  inlineIndent = 16,
  theme,
  className,
  style,
  ...rest
}) => {
  const sider = useContext(SiderContext)
  // antd: an inline Menu inside a collapsed Sider collapses without being told.
  const collapsed = inlineCollapsed ?? (mode === 'inline' && Boolean(sider?.collapsed))

  const [selected, setSelected] = useControllableValue(selectedKeys, defaultSelectedKeys ?? [])
  const [expanded, setExpanded] = useControllableValue(openKeys, defaultOpenKeys ?? [], onOpenChange)

  const toggleSubmenu = (key) => {
    const next = expanded.includes(key)
      ? expanded.filter((item) => item !== key)
      : [...expanded, key]
    setExpanded(next)
  }

  const activate = (item) => {
    if (item.disabled) return
    setSelected([item.key])
    onClick?.({ key: item.key, keyPath: [item.key], item, domEvent: null })
    onSelect?.({ key: item.key, selectedKeys: [item.key], item })
  }

  const holdsSelection = (item) =>
    (item.children ?? []).some(
      (child) => selected.includes(child.key) || holdsSelection(child)
    )

  const renderItem = (item, depth = 0) => {
    if (!item) return null
    if (item.type === 'divider') return <MenuDivider key={item.key} />

    if (item.type === 'group') {
      return (
        <li key={item.key} className="ui-menu-item-group">
          {!collapsed && item.label ? (
            <div className="ui-menu-item-group-title px-3 py-1.5 text-xs uppercase tracking-wide text-[var(--text-muted)]">
              {item.label}
            </div>
          ) : null}
          <ul className="ui-menu-item-group-list m-0 list-none p-0">
            {item.children?.map((child) => renderItem(child, depth))}
          </ul>
        </li>
      )
    }

    const indent = collapsed ? 12 : 12 + depth * inlineIndent

    if (item.children?.length) {
      const isOpen = expanded.includes(item.key)

      return (
        <li
          key={item.key}
          className={cn(
            'ui-menu-submenu ui-menu-submenu-inline',
            isOpen && 'ui-menu-submenu-open',
            holdsSelection(item) && 'ui-menu-submenu-selected',
            item.disabled && 'ui-menu-submenu-disabled'
          )}
        >
          <CollapsedLabel collapsed={collapsed} label={item.label}>
            <div
              role="button"
              tabIndex={item.disabled ? -1 : 0}
              aria-expanded={isOpen}
              aria-disabled={item.disabled || undefined}
              onClick={() => !item.disabled && toggleSubmenu(item.key)}
              onKeyDown={(event) =>
                !item.disabled && activateOnKey(event, () => toggleSubmenu(item.key))
              }
              style={{ paddingLeft: indent }}
              className={rowClasses(
                cn(
                  'ui-menu-submenu-title relative w-full pr-10 text-[var(--text-body)]',
                  collapsed && 'justify-center',
                  item.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-[var(--gray-100)]'
                )
              )}
            >
              {item.icon ? (
                <span className="ui-menu-item-icon inline-flex shrink-0 items-center">
                  {item.icon}
                </span>
              ) : null}
              {!collapsed ? (
                <span className="ui-menu-title-content min-w-0 truncate">{item.label}</span>
              ) : null}
              {!collapsed ? <SubmenuArrow open={isOpen} /> : null}
            </div>
          </CollapsedLabel>

          {!collapsed ? (
            <div
              className={cn(
                'ui-menu-submenu-panel grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
              aria-hidden={!isOpen}
              {...(!isOpen ? { inert: true } : {})}
            >
              <ul
                className={cn(
                  'ui-menu ui-menu-sub ui-menu-inline m-0 min-h-0 list-none overflow-hidden p-0',
                  'transition-opacity duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]',
                  isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
              >
                {item.children.map((child) => renderItem(child, depth + 1))}
              </ul>
            </div>
          ) : null}
        </li>
      )
    }

    const isSelected = selected.includes(item.key)

    // Children stay flat (icon span, then title span) because the sidemenu CSS
    // sizes the icon with `.ui-menu-item > span:first-child`.
    return (
      <CollapsedLabel key={item.key} collapsed={collapsed} label={item.label}>
        <li
          role="menuitem"
          tabIndex={item.disabled ? -1 : 0}
          aria-current={isSelected ? 'page' : undefined}
          aria-disabled={item.disabled || undefined}
          onClick={() => activate(item)}
          onKeyDown={(event) => activateOnKey(event, () => activate(item))}
          style={{ paddingLeft: indent }}
          className={rowClasses(
            cn(
              'ui-menu-item relative w-full',
              collapsed && 'justify-center',
              isSelected
                ? 'ui-menu-item-selected bg-[var(--color-primary-50)] text-[var(--primary-color)]'
                : 'text-[var(--text-body)]',
              item.disabled
                ? 'ui-menu-item-disabled cursor-not-allowed opacity-50'
                : !isSelected && 'hover:bg-[var(--gray-100)]'
            )
          )}
        >
          {item.icon ? (
            <span className="ui-menu-item-icon inline-flex shrink-0 items-center">
              {item.icon}
            </span>
          ) : null}
          {!collapsed ? (
            <span className="ui-menu-title-content min-w-0 truncate">{item.label}</span>
          ) : null}
        </li>
      </CollapsedLabel>
    )
  }

  return (
    <ul
      role="menu"
      className={cn(
        'ui-menu ui-menu-root m-0 list-none p-0',
        `ui-menu-${mode}`,
        collapsed && 'ui-menu-inline-collapsed',
        theme === 'dark' && 'ui-menu-dark',
        className
      )}
      style={style}
      {...rest}
    >
      {items.map((item) => renderItem(item))}
    </ul>
  )
}

Menu.Item = Item
Menu.SubMenu = SubMenu
Menu.ItemGroup = ItemGroup
Menu.Divider = MenuDivider

export default Menu
